/**
 * One leg's stop or target, as a control — ROADMAP 7.5.
 *
 * Shared by the two places a leg can be armed: the confirmation, before the
 * basket is sent (`LegEditor`), and an open position in the drawer
 * (`TradeCards`). One implementation, because the two would drift about what
 * an empty box means the first time either was touched — and what it means
 * is the difference between no protection and a stop at zero.
 */

import { cn } from '@/lib/utils'
import type { ProtectionSpec } from '@apna/shared'

/**
 * PERCENT BY DEFAULT, because that is how this desk already thinks.
 *
 * "Stop decided at 40–50% premium rise" is in the shipped checklist for a
 * Short Straddle, and an absolute price is the one basis a trader cannot
 * decide before they know the fill. The other two are a click away rather
 * than a form field, so the common case costs nothing.
 */
export const BASES: readonly ProtectionSpec['basis'][] = ['PERCENT', 'POINTS', 'PRICE']
const BASIS_LABEL: Record<ProtectionSpec['basis'], string> = {
  PERCENT: '%',
  POINTS: 'pt',
  PRICE: '\u20b9',
}

/**
 * One protection field: a number, and the basis it is measured in.
 *
 * Empty clears it. A trader removing a stop types nothing rather than
 * hunting for a control that says "none", and an empty box is unambiguous
 * in a way a zero is not — zero is a price.
 */
export function ProtectionInput({
  label,
  value,
  tone,
  onChange,
  testId,
}: {
  label: string
  value: ProtectionSpec | null
  tone: 'sl' | 'tgt'
  onChange: (next: ProtectionSpec | null) => void
  testId: string
}) {
  const basis = value?.basis ?? 'PERCENT'
  return (
    <span className="inline-flex items-center overflow-hidden rounded border border-border bg-background">
      <input
        type="number"
        min={0}
        step="any"
        value={value === null ? '' : value.value}
        placeholder={label}
        aria-label={`${label} for this leg`}
        data-testid={testId}
        onChange={(e) => {
          const raw = e.target.value.trim()
          if (raw === '') {
            onChange(null)
            return
          }
          const v = Number(raw)
          // A non-positive stop is not a stop. Refused as input rather than
          // stored and rejected later by `parseProtectionSpec`.
          onChange(Number.isFinite(v) && v > 0 ? { basis, value: v } : null)
        }}
        className={cn(
          'num h-6 w-12 bg-transparent px-1 text-[11px] outline-none placeholder:text-muted-foreground/60',
          tone === 'sl' ? 'text-destructive' : 'text-success',
        )}
      />
      <button
        type="button"
        // Cycles rather than opening a menu: three options, and the label is
        // the control.
        onClick={() =>
          onChange({
            basis: BASES[(BASES.indexOf(basis) + 1) % BASES.length]!,
            value: value?.value ?? 0,
          })
        }
        disabled={value === null}
        title={`Measured in ${basis.toLowerCase()} — click to change`}
        data-testid={`${testId}-basis`}
        className="h-6 border-l border-border px-1 text-[9px] font-bold text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
      >
        {BASIS_LABEL[basis]}
      </button>
    </span>
  )
}

